import { useState } from "react";
import { Loader2, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { ukDateTime } from "../../lib/format";
import { Loading } from "../common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const ROLES = ["Admin", "Approver", "GM", "Reporting", "Finance", "Senior stakeholder", "Partner"];

function UserDialog({ open, onOpenChange, user, partners, onDone }) {
  const editing = !!user;
  const [f, setF] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const init = () => { setErr(""); setF(user ? { name: user.name, roles: user.roles, partner_id: user.partner_id || "", password: "" } : { name: "", email: "", roles: [], partner_id: "", password: "" }); };
  const toggle = (r) => setF((s) => ({ ...s, roles: s.roles.includes(r) ? s.roles.filter((x) => x !== r) : [...s.roles, r] }));
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const body = { name: f.name, roles: f.roles, partner_id: f.roles.includes("Partner") ? f.partner_id || null : null };
      if (editing) { if (f.password) body.password = f.password; await api.patch(`/admin/users/${user.id}`, body); }
      else await api.post("/admin/users", { ...body, email: f.email, password: f.password });
      toast.success(editing ? "User updated" : "User created"); onOpenChange(false); onDone();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) init(); onOpenChange(o); }}>
      <DialogContent onOpenAutoFocus={init}>
        <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${user.name}` : "Add a user"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-form-name" /></div>
          {!editing && <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-form-email" /></div>}
          <div className="space-y-2"><Label>Roles</Label>
            <div className="grid grid-cols-3 gap-2">{ROLES.map((r) => (
              <label key={r} className="flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm cursor-pointer">
                <Checkbox checked={(f.roles || []).includes(r)} onCheckedChange={() => toggle(r)} data-testid={`user-form-role-${r.toLowerCase()}`} /> {r}
              </label>))}
            </div>
          </div>
          {(f.roles || []).includes("Partner") && (
            <div className="space-y-1.5"><Label>JV partner</Label>
              <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={f.partner_id} onChange={(e) => setF({ ...f, partner_id: e.target.value })} data-testid="user-form-partner">
                <option value="">Choose a partner…</option>{partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1.5"><Label>{editing ? "New password (optional)" : "Initial password (min 8 characters)"}</Label>
            <Input type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-form-password" /></div>
          {err && <div className="rounded-md border border-primary/40 bg-accent px-3 py-2 text-sm text-accent-foreground" data-testid="user-form-error">{err}</div>}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="user-form-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Save" : "Create user"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UsersTab() {
  const { data, loading, reload } = useApi("/admin/users");
  const partners = useApi("/partners", { live: false });
  const [dlg, setDlg] = useState({ open: false, user: null });
  if (loading && !data) return <Loading />;
  const setDisabled = async (u, disabled) => {
    try { await api.patch(`/admin/users/${u.id}`, { disabled }); toast.success(disabled ? `${u.name} disabled` : `${u.name} enabled`); reload(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="users-tab">
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{data.length} users. Role changes apply on the user's next request.</p>
        <Button onClick={() => setDlg({ open: true, user: null })} data-testid="add-user-btn"><UserPlus className="mr-2 h-4 w-4" /> Add user</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card grid-scroll">
        <table className="dt w-full text-sm" data-testid="users-table">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Roles</th><th className="px-4 py-2.5">Partner</th><th className="px-4 py-2.5">Last sign-in</th><th className="px-4 py-2.5">Active</th><th></th>
          </tr></thead>
          <tbody>{data.map((u) => (
            <tr key={u.id} className={`border-b last:border-0 ${u.disabled ? "opacity-60" : ""}`} data-testid={`user-row-${u.email}`}>
              <td className="px-4 py-3 font-semibold">{u.name}{u.disabled && <span className="ml-2 rounded bg-accent px-1.5 py-0.5 text-[10px] font-bold uppercase text-accent-foreground" data-testid={`user-disabled-badge-${u.email}`}>Disabled</span>}</td><td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{u.roles.map((r) => <span key={r} className="rounded bg-muted px-1.5 py-0.5 text-xs font-semibold" data-testid="user-role-chip">{r}</span>)}</div></td>
              <td className="px-4 py-3 text-muted-foreground">{u.partner_name || "–"}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{u.last_login ? ukDateTime(u.last_login) : "Never"}</td>
              <td className="px-4 py-3"><Switch checked={!u.disabled} onCheckedChange={(v) => setDisabled(u, !v)} data-testid={`user-active-toggle-${u.email}`} /></td>
              <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => setDlg({ open: true, user: u })} data-testid={`user-edit-btn-${u.email}`}>Edit</Button></td>
            </tr>))}
          </tbody>
        </table>
      </div>
      <UserDialog open={dlg.open} user={dlg.user} partners={partners.data || []} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onDone={() => reload(true)} />
    </div>
  );
}

export { Plus };
